import { Input, Select, TextArea, UnsavedChangesDialog, useUnsavedChangesWarning } from '@viapeople/components'
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import type { FeatureFlagFormInput, FeatureFlagRegistryEntryView } from '../types'

interface FeatureFlagFormProps {
	initialValues: FeatureFlagFormInput
	onSubmit: (data: FeatureFlagFormInput) => Promise<void>
	isSubmitting?: boolean
	isEditMode?: boolean
	availableEntries?: FeatureFlagRegistryEntryView[]
	isLoadingAvailableEntries?: boolean
}

export function FeatureFlagForm({
	initialValues,
	onSubmit,
	isSubmitting = false,
	isEditMode = false,
	availableEntries = [],
	isLoadingAvailableEntries = false,
}: FeatureFlagFormProps) {
	const {
		handleSubmit,
		control,
		setValue,
		formState: { isDirty },
	} = useForm<FeatureFlagFormInput>({
		defaultValues: initialValues,
		mode: 'onChange',
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const formId = isEditMode ? 'editFeatureFlagForm' : 'addFeatureFlagForm'

	// Sticky-edit autofill: once the user manually edits Name/Description after a
	// dropdown selection, do not overwrite their input on subsequent re-selects.
	// Refs are reset on unmount so re-opening the modal starts fresh.
	const userEditedName = useRef(false)
	const userEditedDescription = useRef(false)

	useEffect(() => {
		return () => {
			userEditedName.current = false
			userEditedDescription.current = false
		}
	}, [])

	const noKeysAvailable = !isEditMode && !isLoadingAvailableEntries && availableEntries.length === 0
	let keyPlaceholder = 'Select a feature flag key'
	if (isLoadingAvailableEntries) {
		keyPlaceholder = 'Loading available keys…'
	} else if (noKeysAvailable) {
		keyPlaceholder = 'No feature flag keys available — add one to the registry first'
	}

	return (
		<form id={formId} className='space-y-6' onSubmit={handleSubmit(onSubmit)}>
			<div className='gap-4 flex flex-col'>
				{isEditMode ? (
					<Input label='Key' name='key' value={initialValues.key} disabled readOnly />
				) : (
					<Controller
						name='key'
						control={control}
						rules={{ required: 'Key is required' }}
						render={({ field, fieldState }) => (
							<Select
								label='Key'
								searchable
								placeholder={keyPlaceholder}
								options={availableEntries.map((entry) => ({
									value: entry.key,
									label: `${entry.key} — ${entry.name}`,
								}))}
								value={field.value || undefined}
								onChange={(value: string) => {
									field.onChange(value)
									const entry = availableEntries.find((candidate) => candidate.key === value)
									if (!entry) return
									// Autofill must not flip the form to "dirty" — otherwise the
									// unsaved-changes guard fires after a single dropdown pick.
									if (!userEditedName.current) {
										setValue('name', entry.name, { shouldValidate: true, shouldDirty: false })
									}
									if (!userEditedDescription.current) {
										setValue('description', entry.description, { shouldValidate: true, shouldDirty: false })
									}
								}}
								hint={fieldState.error?.message}
								variant={fieldState.error ? 'error' : 'default'}
								disabled={isSubmitting || noKeysAvailable || isLoadingAvailableEntries}
							/>
						)}
					/>
				)}
				<Controller
					name='name'
					control={control}
					rules={{ required: 'Name is required' }}
					render={({ field, fieldState }) => (
						<Input
							label='Name'
							placeholder='e.g., New Dashboard UI'
							name={field.name}
							value={field.value || ''}
							onChange={(e) => {
								userEditedName.current = true
								field.onChange(e)
							}}
							onBlur={field.onBlur}
							hint={fieldState.error?.message}
							variant={fieldState.error ? 'error' : 'default'}
							disabled={isSubmitting}
						/>
					)}
				/>
				<Controller
					name='description'
					control={control}
					rules={isEditMode ? undefined : { required: 'Description is required' }}
					render={({ field, fieldState }) => (
						<TextArea
							label='Description'
							placeholder='Describe what this feature flag controls'
							name={field.name}
							value={field.value || ''}
							onChange={(e) => {
								userEditedDescription.current = true
								field.onChange(e)
							}}
							onBlur={field.onBlur}
							hint={fieldState.error?.message}
							variant={fieldState.error ? 'error' : 'default'}
							disabled={isSubmitting}
						/>
					)}
				/>
			</div>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
